import { menuItems } from './menuItems';
import { getTabName, getTabTranslationKey, isMenuFolder } from './menuSearch';

function flattenMenuEntries(items, parent = null, acc = []) {
  (items || []).forEach((item) => {
    acc.push({ item, parent });
    if (item.children?.length) flattenMenuEntries(item.children, item, acc);
  });
  return acc;
}

let cachedEntries;

function menuEntries() {
  if (!cachedEntries) cachedEntries = flattenMenuEntries(menuItems);
  return cachedEntries;
}

function collectLabels(item, parent, t) {
  const tr = typeof t === 'function' ? t : (k) => k;
  return new Set(
    [
      item.title,
      item.tabName,
      item.page,
      tr(item.title),
      item.tabName ? tr(item.tabName) : null,
      getTabName(item, parent?.title, t),
    ]
      .filter(Boolean)
      .map((value) => String(value).trim()),
  );
}

/**
 * Resolve a live locale label for Favorites / Last Used from menu identity (page),
 * without rewriting stored user data. Groups and unknown pages keep item.name.
 */
export function getPersistedMenuDisplayName(persisted, t) {
  if (!persisted) return '';
  if (Array.isArray(persisted.favorites)) return persisted.name || '';
  const page = persisted.page;
  if (!page) return persisted.name || '';

  const leaves = menuEntries().filter(({ item }) => item.page === page && !isMenuFolder(item));
  if (leaves.length === 1) {
    const { item, parent } = leaves[0];
    return getTabName(item, parent?.title, t);
  }
  if (leaves.length > 1) {
    const stored = String(persisted.name || '').trim();
    const matched = leaves.filter(({ item, parent }) => collectLabels(item, parent, t).has(stored));
    if (matched.length === 1) {
      const { item, parent } = matched[0];
      return getTabName(item, parent?.title, t);
    }
    return persisted.name || '';
  }
  return persisted.name || '';
}

function resolvePersistedMenuLeaf(persisted, t) {
  if (!persisted || Array.isArray(persisted.favorites)) return null;
  const page = persisted.page;
  if (!page) return null;

  const leaves = menuEntries().filter(({ item }) => item.page === page && !isMenuFolder(item));
  if (leaves.length === 1) return leaves[0];
  if (leaves.length > 1) {
    const stored = String(persisted.name || '').trim();
    const matched = leaves.filter(({ item, parent }) => collectLabels(item, parent, t).has(stored));
    if (matched.length === 1) return matched[0];
  }
  return null;
}

/**
 * Tab open payload for Favorites / Last Used: live label plus semantic key when known.
 */
export function getPersistedMenuTabOpenFields(persisted, t) {
  const name = getPersistedMenuDisplayName(persisted, t);
  const explicitKey = persisted?.translationKey || persisted?.title;
  if (explicitKey && typeof explicitKey === 'string' && !Array.isArray(persisted?.favorites)) {
    return { name, translationKey: explicitKey, legacyLabel: name };
  }
  const leaf = resolvePersistedMenuLeaf(persisted, t);
  if (leaf) {
    const translationKey = getTabTranslationKey(leaf.item, leaf.parent?.title, t);
    if (translationKey) {
      return { name, translationKey, legacyLabel: name };
    }
  }
  return { name };
}

export function flattenMenuItemsForI18n(items = menuItems) {
  const acc = [];
  (items || []).forEach((item) => {
    acc.push(item);
    if (item.children?.length) acc.push(...flattenMenuItemsForI18n(item.children));
  });
  return acc;
}
